import { CurrentUser } from "@/types/user"
import { UserWithFields } from "./../types/user"
import { Component, Vue, Watch, Prop, InjectReactive } from "vue-property-decorator"
import { DataType, dataTypeByFieldType, TemplateDetails, TimeZone } from "@/types"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { DayOfMonth, DayOfWeek, Schedule, ScheduleFreq } from "@/types/schedule"

import moment from "moment"

import api from "@/services/api"

import DateTime from "@/components/Fields/DateTime.vue"
import CreateForTeam from "@/components/Config/Schedules/CreateForTeam.vue"
import FieldAndValue from "@/components/Controls/FieldAndValue.vue"
import ScheduleSetting, { LabeledValue } from "@/types/scheduleSettings"
import { Folder, FolderType } from "@/types/folder"
import _ from "lodash"

export default class ScheduleEditService {
	public templates: TemplateDetails[] = []
	public fields: CaseColumn[] = []
	public timeZones: TimeZone[] = []
	public currentUser: CurrentUser | null = null

	public scheduleSetting: ScheduleSetting = {
		id: undefined,
		tenantId: undefined,
		defaultFieldValues: {}
	};

	// The schedule being edited
	public currentSchedule: Schedule | null = null;

	// The initial template of the schedule being edited
	public initialTemplateId: string | null = null

	public selectedTemplate: TemplateDetails | null = null
	public availableFields: CaseColumn[] | null = null
	private folders: { [title:string]: Folder } = {}

	private generatingCase: boolean = false
	private filterOutCaseColumns = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated", "hiddenFromInsights", "deleted"]

	private get scheduleFreqOptions () {
		return [
			{
				title: "Once",
				id: ScheduleFreq.Once
			},
			{
				title: "Daily",
				id: ScheduleFreq.Daily
			},
			{
				title: "Weekly",
				id: ScheduleFreq.Weekly
			},
			{
				title: "Monthly",
				id: ScheduleFreq.Monthly
			}
		]
	}

	private get dayOptions () {
		return [
			{
				title: "Monday",
				id: DayOfWeek.Monday
			},
			{
				title: "Tuesday",
				id: DayOfWeek.Tuesday
			},
			{
				title: "Wednesday",
				id: DayOfWeek.Wednesday
			},
			{
				title: "Thursday",
				id: DayOfWeek.Thursday
			},
			{
				title: "Friday",
				id: DayOfWeek.Friday
			},
			{
				title: "Saturday",
				id: DayOfWeek.Saturday
			},
			{
				title: "Sunday",
				id: DayOfWeek.Sunday
			}
		]
	}

	public get availableTemplates () : {id: string, title: string}[] {
		if (!this.templates) return []

		// Only show templates that are marked as 'Available for Scheduling'
		var availableTemplates = this.templates.filter(t => t.templateSchedule && t.templateSchedule.schedulingAvailable && t.templateSchedule.schedulingPermissions)

		// Only show templates that the user has permissions for
		availableTemplates = availableTemplates.filter(t => t.templateSchedule!.schedulingPermissions!.length === 0 ||
			t.templateSchedule!.schedulingPermissions!.some((teamId: any) => this.currentUser!.teams.find((team: any) => team.id === teamId)))

		// Ensure the current schedules template is available
		if (this.currentSchedule && this.currentSchedule.templateId && !availableTemplates.find(t => t.id === this.currentSchedule!.templateId)) {
			var selectedTemplate = this.templates.find(t => t.id === this.currentSchedule!.templateId)
			if (selectedTemplate) {
				availableTemplates.push(selectedTemplate)
			}
		}

		return [
			...availableTemplates.map((template:any) => {
				return {
					id: template.id,
					title: template.title
				}
			})
		].sort((a, b) => a.title.localeCompare(b.title))
	}

	public templateChanged (selectedTemplateId: string | null, applyDefaultValues: boolean) {
		this.selectedTemplate = selectedTemplateId
			? this.templates.find(t => t.id === selectedTemplateId) || null
			: null

		// Set available Fields
		this.availableFields = []
		if (selectedTemplateId) {
			if (this.selectedTemplate) {
				var fieldsInTemplate = this.fields.filter(field => caseColumns.find(f => f.key === field.key) || this.selectedTemplate!.fields.find(f => f.field.id === field.key))

				// Check if the fields are available in template scheduling
				if (this.selectedTemplate!.templateSchedule && this.selectedTemplate!.templateSchedule!.schedulingFields!.length !== 0) {
					this.availableFields = fieldsInTemplate.filter(field => this.selectedTemplate!.templateSchedule!.schedulingFields!.find(f => f === field.key))
				} else {
					this.availableFields = fieldsInTemplate
				}
			} else {
				// If template can't be found, allow anything just in case
				this.availableFields = this.fields
			}
		}

		if (!this.currentSchedule!.fields) {
			return
		}

		if (selectedTemplateId) {
			// Remove any fields that are not available
			Object.keys(this.currentSchedule!.fields).forEach(fieldId => {
				if (!this.availableFields!.find(f => f.key === fieldId)) {
					delete this.currentSchedule!.fields[fieldId]
				}
			})
		}

		if (applyDefaultValues) {
			// Apply field defaults if the field has not been set
			this.availableFields!.forEach(f => {
				if (!this.currentSchedule!.fields[f.key]) {
					if (this.selectedTemplate && this.selectedTemplate.templateSchedule && this.selectedTemplate.templateSchedule!.defaultValues![f.key]) {
						// Apply Template Specific default values
						this.currentSchedule!.fields[f.key] = this.selectedTemplate!.templateSchedule!.defaultValues![f.key]
					} else if (this.scheduleSetting!.defaultFieldValues[f.key]) {
						// Apply generic default values
						this.currentSchedule!.fields[f.key] = this.scheduleSetting!.defaultFieldValues[f.key]
					}
				}
			})
		}

		// Sort current schedule fields.
		if (this.currentSchedule && this.currentSchedule.fields && this.selectedTemplate && this.selectedTemplate.fields) {
			this.currentSchedule.fields = Object.keys(this.currentSchedule.fields)
				.sort((a, b) => {
					const fieldA = this.selectedTemplate!.fields.find(f => f.field.id === a)
					const fieldB = this.selectedTemplate!.fields.find(f => f.field.id === b)

					if (!fieldA && !fieldB) {
						return 0	// a and b not found
					}

					if (!!fieldA && !fieldB) {
						return 1	// b not found
					}

					if (!fieldA && !!fieldB) {
						return -1	// a not found
					}

					return fieldA!.order - fieldB!.order
				})
				.reduce((obj: {[key: string]: LabeledValue}, key: string) => {
					obj[key] = this.currentSchedule!.fields![key]
					return obj
				}, {})
		}
	}

	public get columns () : any[] {
		return [
			{
				key: "folderOrExpandable",
				label: "Folder",
				sortable: false,
				inModal: () => false,
				inTable: true,
				hasTemplate: true
			},
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => (item.title ? null : "Please enter a title")
			},
			{
				key: "templateTitle",
				label: "Template",
				dataType: "list",
				valueColumn: "templateId",
				options: this.availableTemplates,
				errors: (item: any) => (item.templateId ? null : "Please select a template"),
				sortable: true,
				inTable: true,
				tooltip: "Ensure that all relevant templates have been marked as 'Available for Scheduling'"
			},
			{
				key: "status",
				label: "Status",
				sortable: true,
				inTable: true,
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.getPermittedSelectionFolders(),
				sortable: true,
				inTable: false,
				inModal: () => true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				sortable: true,
				dataType: "date-time",
				inModal: () => false,
				inTable: false
			},
			{
				key: "lastUpdatedBy.name",
				label: "Last Updated By",
				sortable: true,
				dataType: "text",
				inModal: () => false,
				inTable: false
			},
			{
				key: "timeZoneId",
				label: "Timezone",
				sortable: true,
				dataType: "list",
				valueColumn: "timeZoneId",
				options: this.timeZones,
				errors: (item: Schedule) => (item.timeZoneId ? null : "Please select a timezone"),
				inTable: false
			},
			{
				key: "scheduleFreq",
				label: "Schedule Frequency",
				sortable: true,
				dataType: "list",
				valueColumn: "scheduleFreq",
				options: this.scheduleFreqOptions,
				errors: (item: any) => (item.scheduleFreq ? null : "Please select a schedule frequency"),
				hasTemplate: true,
				inTable: true
			},
			{
				key: "dayOf",
				label: "Day",
				sortable: true,
				dataType: "list",
				valueColumn: "dayOf",
				options: (item: any) => ((item.scheduleFreq === ScheduleFreq.Weekly) ? this.dayOptions : DayOfMonth),
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.Weekly || item.scheduleFreq === ScheduleFreq.Monthly),
				errors: (item: any) => this.validateDaysOfInput(item.scheduleFreq, item.dayOf, item),
				inTable: false
			},
			{
				key: "date",
				label: "Date",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.Once),
				componentProperties: {
					includeYesterday: false,
					includeToday: false
				},
				errors: (item: any) => (item.scheduleFreq !== ScheduleFreq.Once || (item.date && moment(item.date).isAfter(moment().startOf("day"))) ? null : "Please select a future date"),
				inTable: false
			},
			{
				key: "minRunDate",
				label: "From",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: any) => (item.scheduleFreq !== ScheduleFreq.Once),
				componentProperties: {
					includeYesterday: false,
					includeToday: false,
					enterAsUTC: true
				},
				errors: (item: any) => this.validateMaxMinDates(item),
				hasTemplate: true,
				inTable: true
			},
			{
				key: "maxRunDate",
				label: "Until",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: any) => (item.scheduleFreq !== ScheduleFreq.Once),
				componentProperties: {
					includeYesterday: false,
					includeToday: false,
					enterAsUTC: true
				},
				errors: (item: any) => this.validateMaxMinDates(item),
				inTable: true,
				hasTemplate: true
			},
			{
				key: "startTime",
				label: "Start Time",
				sortable: true,
				dataType: "time",
				errors: (item: any) => (item.startTime ? null : "Please enter a start time")
			},
			{
				key: "endTime",
				label: "End Time",
				sortable: true,
				dataType: "time"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				sortable: true,
				dataType: "text",
				inModal: () => false,
				inTable: true,
				hasTemplate: true
			},
			{
				key: "created",
				label: "Created",
				sortable: true,
				dataType: "date-time",
				inModal: () => false,
				inTable: true,
				hasTemplate: true
			},
			{
				key: "newSchedule",
				label: "",
				sortable: false,
				hasTemplate: true,
				inTable: true,
				inModal: () => false
			},
			{
				key: "bulkCreateForTeams",
				label: "Bulk Create for Teams",
				inTable: false,
				dynamicComponent: CreateForTeam
			},
			{
				key: "bulkCreateUserRole",
				valueColumn: "bulkCreateUserRole",
				label: "Bulk Create - User Role",
				inTable: false,
				inModal: (schedule: Schedule) => schedule.templateId && schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0,
				dataType: "list",
				options: (item: any) => item.templateId
					? this.templates.findAndGet(t => t.id === item.templateId, t => t.fields.filter(f => f.field.dataType === "user-role")
						.map(f => {
							return {
								id: f.field.id,
								title: f.field.title
							}
						}),
					[])
					: []
			},
			{
				key: "bulkCreateSubRole",
				valueColumn: "bulkCreateSubRole",
				label: "Bulk Create - Sub-Role",
				inTable: false,
				inModal: (schedule: Schedule) => schedule.templateId && this.subRolesEnabled(schedule),
				dataType: "list",
				errors: (schedule: Schedule) => this.validateSubRoles(schedule),
				options: (schedule: Schedule) =>
					this.fields
						.findAndGet(f => f.key === schedule.bulkCreateUserRole,
							f => f.fieldExtensions && f.fieldExtensions.subroles ? f.fieldExtensions.subroles.map((subrole: { title: string }) => {
								return {
									id: subrole.title,
									title: subrole.title
								}
							}) : [], [])
			},
			{
				key: "fields",
				label: "Fields",
				inTable: false,
				dynamicComponent: FieldAndValue,
				componentProperties: {
					fields: this.availableFields
				}
			}
		]
	}

	public getPermittedFolders () : { [title:string] : Folder } {
		const userTeamIds = this.currentUser!.teams.map(t => t.id)

		// folders where some of the user's teams are included in the folder's teams
		const permittedFolders = _.pickBy(this.folders, folder =>
			(!folder.teams || folder.teams.length === 0) ||
			folder.teams.some(folderTeamId => userTeamIds.includes(folderTeamId)))
		return permittedFolders
	}

	// needs a hack like this somewhere as saving will default to saving the folder.id to schedule.folder, but it expects the folder title
	private getPermittedSelectionFolders () : {id: string, title: string }[] {
		return Object.values(this.getPermittedFolders())
			.map(folder => { return { id: folder.title, title: folder.title } })
	}

	private validateMaxMinDates (item: Schedule) : string | null {
		if (!(item.minRunDate && item.maxRunDate)) {
			return null
		}

		if (item.minRunDate > item.maxRunDate) {
			return "Minimum schedule date cannot be after maximum"
		}

		return null
	}

	private subRolesEnabled (schedule: Schedule) : boolean {
		return schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 && schedule.bulkCreateUserRole &&
					this.fields.findAndGet(f => f.key === schedule.bulkCreateUserRole, f => f.fieldExtensions && f.fieldExtensions.hasSubroles, false)
	}

	private validateSubRoles (schedule: Schedule) : string | null {
		if (!this.subRolesEnabled(schedule)) {
			return null
		}

		if (schedule.bulkCreateSubRole) {
			return null
		}

		return "Please select a sub-role"
	}

	private validateDaysOfInput (scheduleFreq: ScheduleFreq, dayOf: number | null | undefined, item: Schedule) : string | null {
		if (scheduleFreq === ScheduleFreq.Weekly) {
			if (dayOf === null || dayOf === undefined || dayOf < 0 || dayOf > 6) {
				return "Please select a valid Day of Week"
			}
		}

		if (scheduleFreq === ScheduleFreq.Monthly) {
			if (!dayOf || dayOf < 1 || dayOf > 31) {
				return "Please select a valid Day of Month"
			}
		}

		if (!(item.minRunDate && item.maxRunDate)) {
			return null
		}

		if (scheduleFreq === ScheduleFreq.Weekly) {
			const dates = this.getDatesBetweenTwoDates(item.minRunDate, item.maxRunDate)

			const daysOfWeek = dates.map(c => c.getDay())
			const dayOfIsInRange = (dayOf === 0 || dayOf) && daysOfWeek.includes(dayOf) // 0 is Sunday, so it is valid

			if (!dayOfIsInRange) {
				return "Day of Week selected is not within maximum and minimum dates"
			}
		}

		if (scheduleFreq === ScheduleFreq.Monthly) {
			const dates = this.getDatesBetweenTwoDates(item.minRunDate, item.maxRunDate)
			const daysOfMonth = dates.map(c => c.getDate())

			const dayOfIsInRange = dayOf && daysOfMonth.includes(dayOf)

			if (!dayOfIsInRange) {
				return "Day of Month selected is not within maximum and minimum dates"
			}
		}

		return null
	}

	private getDatesBetweenTwoDates (startDate : Date, stopDate: Date) : Date[] {
		const dateArray: Date[] = []
		let current = moment(startDate)
		const stop = moment(stopDate)
		while (current <= stop) {
			dateArray.push(current.toDate())
			current = current.add(1, "days")
		}
		return dateArray
	}

	public constructor () {

	}

	public async load (currentUser: CurrentUser) {
		const templateLoad = api.templatesWithFields().then(templates => this.templates = templates)
		const timezoneLoad = api.timeZonesGet().then(timeZones => this.timeZones = timeZones)
		const currentUserLoad = currentUser ? Promise.resolve(this.currentUser = currentUser) : api.me().then(user => this.currentUser = user)
		const foldersLoad = this.loadFolders()

		const fieldLoad = api.fields().then(fields => {
			if (!fields) {
				this.fields = []
				return
			}

			// Map the default and created fields
			this.fields = [
				...caseColumns.filter(f => this.filterOutCaseColumns.includes(f.key) === false),
				...(fields ? fields
					.map(f => {
						return {
							label: f.title,
							description: f.description,
							key: f.id!,
							fieldId: f.id,
							dataType: f.dataType!,
							dataSourceId: f.dataSourceId,
							dynamicField: true,
							fieldExtensions: f.extensions,
							fieldDataType: f.dataType === "calculated"
								? DataType.number
								: dataTypeByFieldType[f.dataType!]
									? dataTypeByFieldType[f.dataType!]
									: DataType.string
						}
					})
					: [])
			]
		})

		await Promise.all([templateLoad, timezoneLoad, foldersLoad, fieldLoad, currentUserLoad])
	}

	private async loadFolders () {
		const folders: Folder[] = await api.foldersGet(FolderType.Schedule)
		this.folders = folders.toDictionary("title")
	}

	public async saveSchedule (schedule: Schedule): Promise<void> {
		if (schedule.templateId) {
			schedule.templateTitle = this.templates.find(x => x.id === schedule.templateId)!.title
		}

		if (schedule.id) {
			await api.updateSchedule(schedule)
		} else {
			await api.createSchedule(schedule)
		}
	}

	public async deleteSchedule (schedule: Schedule): Promise<void> {
		await api.deleteSchedule(schedule)
	}
}
